/**
 * Microcourse Studio — 常量定义
 */

import type { MicroElementType, QuizQuestionType } from './types.js'

// ─── Element Type Labels ───────────────────────────────────────────

export const ELEMENT_TYPE_LABELS: Record<MicroElementType, string> = {
  heading: '标题',
  paragraph: '段落',
  quote: '引用',
  list: '列表',
  code: '代码块',
  math: '数学公式',
  image: '图片',
  video: '视频',
  audio: '音频',
  iframe: '网页嵌入',
  table: '表格',
  divider: '分隔线',
  spacer: '留白',
  geometry: '几何模型',
  svgExplain: 'SVG讲解',
  jsxgraphExplain: 'JSXGraph讲解',
  geogebraExplain: 'GeoGebra讲解',
  quiz: '课堂测验',
  excalidraw: '手绘白板',
}

// ─── Quiz Type Labels ──────────────────────────────────────────────

export const QUIZ_TYPE_LABELS: Record<QuizQuestionType, string> = {
  choice: '单选题',
  multi_choice: '多选题',
  fill_blank: '填空题',
  true_false: '判断题',
}

// ─── Slide Templates ───────────────────────────────────────────────

export type SlideTemplateId =
  | 'cover'
  | 'content'
  | 'two-column'
  | 'code-demo'
  | 'exercise'
  | 'summary'
  | 'quote-slide'

export const SLIDE_TEMPLATE_LABELS: Record<SlideTemplateId, string> = {
  cover: '封面页',
  content: '内容页',
  'two-column': '双栏页',
  'code-demo': '代码演示',
  exercise: '练习题',
  summary: '总结页',
  'quote-slide': '名言页',
}

// ─── Theme Registry ────────────────────────────────────────────────

export interface ThemeDefinition {
  id: string
  name: string
  type: 'custom' | 'builtin'
  preview: { bg: string; text: string }
  css: string
}

export const MICRO_THEMES: ThemeDefinition[] = [
  {
    id: 'micro-blue',
    name: '科技蓝',
    type: 'custom',
    preview: { bg: '#2d5aa0', text: '#ffffff' },
    css: `
      :root { --r-background-color: #2d5aa0; --r-main-color: #fff; --r-heading-color: #fff; --r-link-color: #e8a838; }
      .reveal { background: linear-gradient(135deg, #1e3d6f, #2d5aa0); color: #fff; }
      .reveal h1, .reveal h2, .reveal h3 { color: #fff; text-shadow: 0 2px 8px rgba(0,0,0,0.2); }
      .reveal section { padding: 40px; }
      .reveal .element-code { background: rgba(0,0,0,0.3); }
    `,
  },
  {
    id: 'micro-green',
    name: '教育绿',
    type: 'custom',
    preview: { bg: '#2d8659', text: '#ffffff' },
    css: `
      :root { --r-background-color: #2d8659; --r-main-color: #fff; --r-heading-color: #fff; }
      .reveal { background: linear-gradient(135deg, #1a5c3a, #2d8659); color: #fff; }
      .reveal h1, .reveal h2, .reveal h3 { color: #fff; }
      .reveal section { padding: 40px; }
    `,
  },
  {
    id: 'micro-orange',
    name: '活力橙',
    type: 'custom',
    preview: { bg: '#e87838', text: '#ffffff' },
    css: `
      :root { --r-background-color: #e87838; --r-main-color: #fff; --r-heading-color: #fff; }
      .reveal { background: linear-gradient(135deg, #c95f24, #e87838); color: #fff; }
      .reveal h1, .reveal h2, .reveal h3 { color: #fff; }
      .reveal section { padding: 40px; }
    `,
  },
  {
    id: 'micro-purple',
    name: '典雅紫',
    type: 'custom',
    preview: { bg: '#5b3a8c', text: '#ffffff' },
    css: `
      .reveal { background: linear-gradient(135deg, #3d2566, #5b3a8c); color: #fff; }
      .reveal h1, .reveal h2, .reveal h3 { color: #fff; }
      .reveal section { padding: 40px; }
    `,
  },
  {
    id: 'micro-sky',
    name: '天蓝渐变',
    type: 'custom',
    preview: { bg: '#7ec8f0', text: '#1a3a5c' },
    css: `
      .reveal { background: linear-gradient(160deg, #e3f4fd 0%, #b8e2f8 45%, #8ecdf0 100%); color: #1e3a5f; }
      .reveal h1, .reveal h2, .reveal h3 { color: #14548a; }
      .reveal section { padding: 40px; }
      .reveal blockquote { border-left-color: #2e86c1; }
    `,
  },
  {
    id: 'micro-light',
    name: '清爽白',
    type: 'custom',
    preview: { bg: '#ffffff', text: '#2d5aa0' },
    css: `
      .reveal { background: #fff; color: #2c3e50; }
      .reveal h1, .reveal h2, .reveal h3 { color: #2d5aa0; }
      .reveal section { padding: 40px; }
      .reveal .element-quote { border-left-color: #e8a838; }
    `,
  },
  {
    id: 'micro-dark',
    name: '深邃黑',
    type: 'custom',
    preview: { bg: '#1a1a2e', text: '#e8a838' },
    css: `
      .reveal { background: #1a1a2e; color: #e0e0e0; }
      .reveal h1, .reveal h2, .reveal h3 { color: #e8a838; }
      .reveal section { padding: 40px; }
    `,
  },
  { id: 'black', name: '经典黑', type: 'builtin', preview: { bg: '#222222', text: '#ffffff' }, css: '' },
  { id: 'white', name: '经典白', type: 'builtin', preview: { bg: '#ffffff', text: '#222222' }, css: '' },
  { id: 'beige', name: '米色', type: 'builtin', preview: { bg: '#f7f3de', text: '#333333' }, css: '' },
  { id: 'sky', name: '天空蓝', type: 'builtin', preview: { bg: '#87ceeb', text: '#333333' }, css: '' },
  { id: 'night', name: '夜空', type: 'builtin', preview: { bg: '#111111', text: '#eeeeee' }, css: '' },
  { id: 'serif', name: '衬线', type: 'builtin', preview: { bg: '#f0f0f0', text: '#333333' }, css: '' },
  { id: 'simple', name: '简约', type: 'builtin', preview: { bg: '#ffffff', text: '#333333' }, css: '' },
  { id: 'solarized', name: '日光', type: 'builtin', preview: { bg: '#fdf6e3', text: '#657b83' }, css: '' },
  { id: 'blood', name: '暗红', type: 'builtin', preview: { bg: '#222222', text: '#aa2233' }, css: '' },
  { id: 'moon', name: '月光', type: 'builtin', preview: { bg: '#002b36', text: '#93a1a1' }, css: '' },
]

export const DEFAULT_THEME_ID = 'micro-blue'

export function getTheme(id: string): ThemeDefinition | undefined {
  return MICRO_THEMES.find((t) => t.id === id)
}

export function getThemeCSS(id: string): string {
  return getTheme(id)?.css ?? ''
}

// ─── Font Families ─────────────────────────────────────────────────

export interface FontFamilyOption {
  id: string
  name: string
  stack: string
}

export const FONT_FAMILIES: FontFamilyOption[] = [
  { id: 'noto-sans-sc', name: '思源黑体', stack: "'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif" },
  { id: 'pingfang-sc', name: '苹方', stack: "'PingFang SC', 'Microsoft YaHei', sans-serif" },
  { id: 'microsoft-yahei', name: '微软雅黑', stack: "'Microsoft YaHei', 'SimHei', sans-serif" },
  { id: 'serif-song', name: '宋体衬线', stack: "'Noto Serif SC', 'SimSun', 'Songti SC', serif" },
  { id: 'kaiti', name: '楷体', stack: "'Kaiti SC', 'KaiTi', 'STKaiti', serif" },
  { id: 'system-ui', name: '系统默认', stack: 'system-ui, sans-serif' },
  { id: 'mono', name: '等宽字体', stack: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace" },
]

export function getFontFamily(id: string): string {
  return FONT_FAMILIES.find((f) => f.id === id)?.stack ?? DEFAULT_PROJECT_SETTINGS.fontFamily
}

// ─── Transitions ───────────────────────────────────────────────────

export const TRANSITIONS = ['slide', 'fade', 'convex', 'concave', 'zoom', 'none'] as const
export type TransitionType = (typeof TRANSITIONS)[number]

// ─── Format Version ────────────────────────────────────────────────

export const FORMAT_VERSION_LEGACY = 1
export const FORMAT_VERSION_CURRENT = 2

// ─── Default Project Settings ──────────────────────────────────────

export const DEFAULT_PROJECT_SETTINGS = {
  theme: DEFAULT_THEME_ID,
  customColors: { primary: '#2d5aa0', accent: '#e8a838', bg: '#ffffff' },
  fontFamily: "'Noto Sans SC', sans-serif",
  transition: 'slide' as const,
  fontScale: 1.0,
}
